import React from 'react';

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useModalTemplateStore from './hooks/useModalTemplateStore';
import { CancelButton } from './components/CancelButton';
import { ActionButton } from './components/ActionButton';
import { LoadingElement } from '../LoadingElement';

type ModalProps = {
  title: string;
  children: React.ReactNode;
  subtitle?: React.ReactNode;
  isLoading?: boolean;
  extraHeaderContent?: React.ReactNode | (() => React.ReactNode);
};

function ModalTemplate({ title, children, subtitle, isLoading, extraHeaderContent }: ModalProps) {
  const close = useModalTemplateStore((state) => state.close);

  const handleCancelClick = () => {
    close();
  };

  if (React.Children.count(children) > 3) {
    throw new Error('ModalTemplate expects at most 3 children: content, cancel button, and action button.');
  }

  const [content, cancelButton, actionButton] = React.Children.toArray(children);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleCancelClick} open>
      <DialogTitle
        sx={{
          p: 2,
        }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h1">{title}</Typography>
          <IconButton onClick={close} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {subtitle && <FormHelperText>{subtitle}</FormHelperText>}
        <Box sx={{ mt: 2 }}>{typeof extraHeaderContent === 'function' ? extraHeaderContent() : extraHeaderContent}</Box>
      </DialogTitle>

      {isLoading ? (
        <LoadingElement />
      ) : (
        <>
          <DialogContent sx={{ p: 3, border: '1px solid #919EAB33', bgcolor: '#F9FAFB' }}>
            <Box sx={{ mb: 2, paddingTop: 3 }}>{content}</Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            {cancelButton}
            {actionButton}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

const Modal = {
  CancelButton,
  ActionButton,
  Modal: ModalTemplate,
};

export default Modal;
